



import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, ViewEncapsulation, output, inject } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Constants } from '@config/constants';
import { FONT_FAMILIES } from '@config/font-families.constant';
import { Messages } from '@config/messages';
import { AppService } from '@services/app.service';
import { MatRadioModule } from '@angular/material/radio';
import { MatCardModule } from '@angular/material/card';
import { Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { LanguageService } from '@services/language.service';
import { DEFAULT_SELF_SIGN_SIGNATURE_COLOR, recolorSignatureImage } from '@utils/signature-color.util';

@Component({
  selector: 'kzn-upload-sign-initials',
  standalone: true,
  imports: [MatFormFieldModule, MatInputModule, ReactiveFormsModule, MatRadioModule, MatCardModule, FormsModule],
  templateUrl: './upload-sign-initials.component.html',
  styleUrl: './upload-sign-initials.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class UploadSignInitialsComponent implements OnInit, OnChanges, OnDestroy {
  Messages = Messages;
  @ViewChild('canvas') canvas!: ElementRef;
  @Input() signerName: string = '';
  @Input() signatureColor: string = DEFAULT_SELF_SIGN_SIGNATURE_COLOR;
  
  private initialsCtrlValidations = [Validators.required, Validators.minLength(2)]
  initialsCtrl: FormControl = new FormControl('', this.initialsCtrlValidations);
  /** Radio value tracks font family so color refreshes never desync preview boxes. */
  initialImgCtrl: FormControl = new FormControl('');
  
  initials: Array<{
    sourceImage: string;
    image: string;
    fontFamily: string;
  }> = [];

  private fontFamiliesUsed: string[] = [];
  private selectedFontFamily: string | null = null;
  private _initialsGeneration = 0;
  
  onInitialImageChange = output<string>();
  initialsCtrlSubscription!: Subscription;
  private _languageService = inject(LanguageService);
  
  constructor(private appService: AppService) {
    this.initialsCtrlSubscription = this.initialsCtrl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
    ).subscribe(() => {
      if (!this.initialsCtrl.touched) this.initialsCtrl.markAsTouched();
      if (this.initialsCtrl.valid) this.generateInitials();
    })
  }

  ngOnInit(): void {
    if(this.signerName) this.initialsCtrl.setValue(this.signerName);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['signatureColor'] && !changes['signatureColor'].firstChange && this.initials.length) {
      void this.refreshInitialsColor();
    }
  }

  async generateTextToImage(text: string, fontFamily: string): Promise<string> {
  const tCtx: any = this.canvas.nativeElement.getContext("2d");
  const fontSize = 50;
  const font = `600 ${fontSize}px ${fontFamily}`;

  await document.fonts.load(font);

  tCtx.font = font;
  const metrics = tCtx.measureText(text);
  const textWidth = Math.ceil(metrics.width);
  const ascent = Math.ceil(metrics.actualBoundingBoxAscent || fontSize * 0.9);
  const descent = Math.ceil(metrics.actualBoundingBoxDescent || fontSize * 0.5);

  const padX = 48;
  const padY = 28;
  tCtx.canvas.width = textWidth + padX * 5;
  tCtx.canvas.height = ascent + descent + padY * 2;

  tCtx.clearRect(0, 0, tCtx.canvas.width, tCtx.canvas.height);

  tCtx.font = font;
  tCtx.fillStyle = "#000";
  tCtx.textBaseline = "middle";
  tCtx.textAlign = "center";
  tCtx.fillText(text, tCtx.canvas.width / 2, tCtx.canvas.height / 2);

  return tCtx.canvas.toDataURL();
}

  // async generateTextToImage(text: string, fontFamily: string): Promise<string> {
  //   const tCtx: any = this.canvas.nativeElement.getContext("2d");
  //   const font = `600 50px ${fontFamily}`;

  //   await document.fonts.load(font);

  //   tCtx.canvas.width = tCtx.measureText(text).width + 200;
  //   tCtx.canvas.height = 80;
  //   tCtx.clearRect(0, 0, tCtx.canvas.width, tCtx.canvas.height);

  //   tCtx.font = font;
  //   tCtx.fillStyle = DEFAULT_SELF_SIGN_SIGNATURE_COLOR;
  //   tCtx.textBaseline = "middle";
  //   tCtx.textAlign = "center";
  //   tCtx.fillText(text, tCtx.canvas.width / 2, tCtx.canvas.height / 2);

  //   return tCtx.canvas.toDataURL();
  // }

  generateInitials(): void {
    this.initialsCtrl.markAsTouched();
    this.selectedFontFamily =
      this.initialImgCtrl.value?.toString().trim() ||
      this.selectedFontFamily;

    if (!this.initialsCtrl.valid || !this.initialsCtrl.value) {
      this.initials = [];
      return;
    }

    if (!this.fontFamiliesUsed.length) {
      this.fontFamiliesUsed = this.appService.getRandomElements(
        FONT_FAMILIES,
        Constants.DEFAULT_SIGN_INITIALS_COUNT,
      );
    }

    const generation = ++this._initialsGeneration;
    const text = this.initialsCtrl.value;
    const color = this.signatureColor;
    const families = [...this.fontFamiliesUsed];

    void (async () => {
      const nextInitials: Array<{ sourceImage: string; image: string; fontFamily: string }> = [];
      for (const fontFamily of families) {
        const sourceImage = await this.generateTextToImage(text, fontFamily);
        const image = await this.colorInitialImage(sourceImage, color);
        if (generation !== this._initialsGeneration) return;
        nextInitials.push({ sourceImage, image, fontFamily });
      }

      if (generation !== this._initialsGeneration) return;
      this.initials = nextInitials;
      this.restoreInitialSelection(true);
    })();
  }

  private async colorInitialImage(sourceImage: string, color: string): Promise<string> {
    const normalized = (color ?? '').trim().toLowerCase();
    if (!normalized || normalized === DEFAULT_SELF_SIGN_SIGNATURE_COLOR.toLowerCase()) {
      return sourceImage;
    }
    try {
      return await recolorSignatureImage(sourceImage, color);
    } catch {
      return sourceImage;
    }
  }

  async refreshInitialsColor(): Promise<void> {
    if (!this.initials.length) return;

    const generation = ++this._initialsGeneration;
    const color = this.signatureColor;
    const snapshot = this.initials.map((initial) => ({ ...initial }));

    const refreshed: Array<{ sourceImage: string; image: string; fontFamily: string }> = [];
    for (const initial of snapshot) {
      const image = await this.colorInitialImage(initial.sourceImage, color);
      if (generation !== this._initialsGeneration) return;
      refreshed.push({
        sourceImage: initial.sourceImage,
        image,
        fontFamily: initial.fontFamily,
      });
    }

    if (generation !== this._initialsGeneration) return;
    this.initials = refreshed;
    this.restoreInitialSelection(true);
  }

  private restoreInitialSelection(emitChange = false): void {
    const fontFamily = this.selectedFontFamily || this.initialImgCtrl.value?.toString().trim();
    if (!fontFamily) return;

    const match = this.initials.find((item) => item.fontFamily === fontFamily);
    if (!match) return;

    this.selectedFontFamily = match.fontFamily;
    this.initialImgCtrl.setValue(match.fontFamily, { emitEvent: false });
    if (emitChange) {
      this.onInitialImageChange.emit(match.sourceImage);
    }
  }

  initialImageChange(): void {
    const fontFamily = this.initialImgCtrl.value?.toString().trim();
    const selected = this.initials.find((item) => item.fontFamily === fontFamily);
    this.selectedFontFamily = selected?.fontFamily ?? null;
    this.onInitialImageChange.emit(selected?.sourceImage ?? '');
  }

  resetInitials(): void {
    ++this._initialsGeneration;
    this.initialsCtrl.reset();
    this.initialsCtrl.setErrors(null);
    this.initialImgCtrl.reset();
    this.initials = [];
    this.fontFamiliesUsed = [];
    this.selectedFontFamily = null;
  }

  ngOnDestroy(): void {
    this.initialsCtrlSubscription?.unsubscribe();
  }

  t(key: string): string {
    return this._languageService.instant(key);
  }
}
